import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CreditCard, Clock, Zap, ClipboardList, Star, ArrowLeft, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { apiFetch } from "@/lib/api";
import { useNavigate } from "react-router-dom";

interface HistoryEvent {
  id: string;
  event_type: string;
  description: string;
  plan: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const EVENT_CONFIG: Record<string, { icon: typeof CreditCard; colorClass: string; dotClass: string }> = {
  plan_purchased: { icon: CreditCard, colorClass: "text-green-600", dotClass: "bg-green-500" },
  plan_expired: { icon: Clock, colorClass: "text-destructive", dotClass: "bg-destructive" },
  credit_used: { icon: Zap, colorClass: "text-orange-500", dotClass: "bg-orange-500" },
  diet_generated: { icon: ClipboardList, colorClass: "text-blue-500", dotClass: "bg-blue-500" },
  credit_purchased: { icon: Star, colorClass: "text-blue-500", dotClass: "bg-blue-500" },
};

const PLAN_NAMES: Record<string, string> = {
  basic: "PRO FIT",
  plus: "PRO FIT PLUS",
  complete: "PRO FIT MAX",
};

const PLAN_BADGE_CLASS: Record<string, string> = {
  basic: "bg-blue-500/15 text-blue-600 border-blue-500/20",
  plus: "bg-purple-500/15 text-purple-600 border-purple-500/20",
  complete: "bg-amber-500/15 text-amber-600 border-amber-500/20",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PurchaseHistory = () => {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch("api-purchase-history");
        setEvents(res.data || []);
      } catch (e) {
        console.error("Failed to load purchase history:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/profile")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-bold text-foreground">Histórico de Compras</h1>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4 p-4">
              <Skeleton className="w-10 h-10 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-card rounded-2xl p-8 shadow-card border border-border text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto">
            <ShoppingBag className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">Nenhuma compra realizada ainda.</p>
          <Button onClick={() => navigate("/plans")} className="gradient-primary text-primary-foreground">
            Ver planos
          </Button>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

          <div className="space-y-1">
            {events.map((event, idx) => {
              const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.diet_generated;
              const Icon = config.icon;

              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="relative flex gap-4 py-4 pl-1"
                >
                  {/* Dot */}
                  <div className={`w-10 h-10 rounded-full ${config.dotClass}/15 flex items-center justify-center shrink-0 z-10 border-2 border-background`}>
                    <Icon className={`w-4 h-4 ${config.colorClass}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{event.description}</p>
                      {event.plan && PLAN_NAMES[event.plan] && (
                        <Badge className={`text-[10px] ${PLAN_BADGE_CLASS[event.plan] || ""}`}>
                          {PLAN_NAMES[event.plan]}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(event.created_at)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PurchaseHistory;
